import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { ThemeContext } from '../contexts/ThemeContext';
import { LanguageContext } from '../contexts/LanguageContext';

const ServiceCard = ({ title, description, link }) => {
  const { translations } = useContext(LanguageContext);
  const { theme } = useContext(ThemeContext);
  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md hover:shadow-lg transition border border-gray-100 dark:border-gray-700">
      <h3 className="text-xl font-semibold text-blue-900 dark:text-blue-100 mb-3">{title}</h3>
      <p className="text-gray-700 dark:text-gray-300 mb-4">{description}</p>
      <Link to={link} className="text-blue-600 dark:text-blue-400 font-medium hover:underline inline-flex items-center">
        {translations['services.learnMore'] || 'Learn more'}
        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
        </svg>
      </Link>
    </div>
  );
};

const Services = () => {
  const { theme } = useContext(ThemeContext);
  const { translations } = useContext(LanguageContext);
  const services = [
    {
      key: 'platform-engineering',
      title: translations['services.platform.title'] || translations['services.platformEngineering.title'] || 'Platform Engineering',
      description: translations['services.platform.description'] || translations['services.platformEngineering.description'] || 'We provide a full-service platform engineering solution from design to implementation, allowing your teams to focus on delivering business value through applications.',
      link: "/platform-engineering"
    },
    {
      key: 'managed-openshift',
      title: translations['services.openshift.title'] || translations['services.managedOpenshift.title'] || 'Managed OpenShift',
      description: translations['services.openshift.description'] || translations['services.managedOpenshift.description'] || 'Let our certified experts handle the complexity of managing OpenShift clusters so your team can concentrate on core business functions.',
      link: "/managed-openshift"
    },
    {
      key: 'kubernetes-consulting',
      title: translations['services.consulting.title'] || translations['services.kubernetesConsulting.title'] || 'Kubernetes Consulting',
      description: translations['services.consulting.description'] || translations['services.kubernetesConsulting.description'] || 'Our consultants work with your team to implement best practices, optimize workflows, and ensure your Kubernetes infrastructure is secure and efficient.',
      link: "/kubernetes-consulting"
    },
    {
      key: 'enterprise-observability',
      title: translations['services.observability.title'] || translations['services.enterpriseObservability.title'] || 'Enterprise Observability',
      description: translations['services.observability.description'] || translations['services.enterpriseObservability.description'] || 'Gain real-time insights across your entire infrastructure with our advanced monitoring, logging, and tracing solutions that ensure optimal performance and rapid incident resolution.',
      link: "/observability"
    },
    {
      key: 'risk-consulting',
      title: translations['services.risk.title'] || translations['services.riskConsulting.title'] || 'Risk Consulting',
      description: translations['services.risk.description'] || translations['services.riskConsulting.description'] || 'Our comprehensive risk assessment frameworks help identify, analyze, and mitigate potential security vulnerabilities in your Kubernetes environment.',
      link: "/risk-consulting"
    },
  ];

  return (
    <section className="bg-white dark:bg-gray-900 pt-0 pb-16">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 dark:text-blue-50 mb-6">{translations['services.heading']}</h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 max-w-2xl mx-auto">
            {translations['services.subheading']}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <ServiceCard key={service.key} {...service} />
          ))}
        </div>
        
        <div className="text-center mt-10">
          <Link 
            to="/contact" 
            className="inline-flex items-center bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white font-semibold px-6 py-3 rounded-md transition transform hover:scale-105"
          >
            {translations['services.contactCta'] || 'Contact to learn more'}
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Services;